/**
 * confirmService · Diálogo de confirmación global unificado.
 * iter35 · Reemplaza window.confirm() por un modal futurista consistente
 * con el tema BIGTIME. Uso:
 *
 *   import { btConfirm } from '@/services/confirmService';
 *   if (!(await btConfirm('¿Eliminar este pedido?'))) return;
 *
 * También expuesto como window.btConfirm para llamadas ad-hoc.
 */

let mounted = false;

function ensureRoot() {
  if (mounted) return;
  mounted = true;
}

/**
 * Muestra el modal y resuelve con true/false. Reusa un único nodo en el body.
 * El diseño (dark + amarillo + rojo) va inline para no depender de Tailwind
 * runtime (el modal debe funcionar aún si se llama dentro de un contenedor
 * con clases scoped).
 */
export function btConfirm(message, opts = {}) {
  ensureRoot();
  const {
    title = '¿Estás seguro?',
    body = message || '¿Estás seguro que deseas eliminar esto? Esta acción no se puede deshacer.',
    confirmLabel = 'Eliminar',
    cancelLabel = 'Cancelar',
    variant = 'danger', // danger|primary
  } = typeof message === 'string' ? { body: message, ...opts } : (message || {});

  return new Promise((resolve) => {
    // Root overlay
    const overlay = document.createElement('div');
    overlay.setAttribute('data-testid', 'bt-confirm-overlay');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0',
      background: 'rgba(3, 6, 12, .78)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: '99999', padding: '16px',
      opacity: '0', transition: 'opacity .2s ease',
    });

    // Card
    const card = document.createElement('div');
    Object.assign(card.style, {
      background: 'linear-gradient(180deg, #1a2332 0%, #131a24 100%)',
      color: '#E6EDF3', border: '1px solid rgba(148,163,184,.22)',
      borderRadius: '14px', padding: '20px', maxWidth: '420px', width: '100%',
      boxShadow: '0 20px 60px -20px rgba(0,0,0,.7), 0 0 24px -6px rgba(239, 68, 68, .35)',
      transform: 'scale(.95) translateY(6px)', transition: 'transform .2s ease',
      fontFamily: "'Inter','Segoe UI',system-ui,sans-serif",
    });

    // Icon
    const iconRow = document.createElement('div');
    Object.assign(iconRow.style, { display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '10px' });
    const iconEl = document.createElement('div');
    iconEl.textContent = variant === 'danger' ? '⚠️' : '❔';
    Object.assign(iconEl.style, {
      fontSize: '28px', width: '46px', height: '46px', flexShrink: '0',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: variant === 'danger' ? 'rgba(239,68,68,.15)' : 'rgba(248,209,63,.15)',
      border: variant === 'danger' ? '1px solid rgba(239,68,68,.4)' : '1px solid rgba(248,209,63,.4)',
      borderRadius: '10px',
    });
    const titleWrap = document.createElement('div');
    const h = document.createElement('h3');
    h.textContent = title;
    Object.assign(h.style, { margin: '0 0 4px', fontSize: '17px', fontWeight: '800', color: '#FEF9D7' });
    const p = document.createElement('p');
    p.textContent = body;
    Object.assign(p.style, { margin: 0, fontSize: '14px', color: '#C7D2E0', lineHeight: '1.4' });
    titleWrap.appendChild(h);
    titleWrap.appendChild(p);
    iconRow.appendChild(iconEl);
    iconRow.appendChild(titleWrap);
    card.appendChild(iconRow);

    // Buttons
    const btnRow = document.createElement('div');
    Object.assign(btnRow.style, { display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' });
    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = cancelLabel;
    cancelBtn.setAttribute('data-testid', 'bt-confirm-cancel');
    Object.assign(cancelBtn.style, {
      padding: '9px 16px', borderRadius: '999px', fontWeight: '700', fontSize: '13px',
      border: '1px solid rgba(148,163,184,.25)', background: 'transparent', color: '#C7D2E0',
      cursor: 'pointer', transition: 'background .15s ease',
    });
    cancelBtn.onmouseenter = () => { cancelBtn.style.background = 'rgba(148,163,184,.1)'; };
    cancelBtn.onmouseleave = () => { cancelBtn.style.background = 'transparent'; };

    const okBtn = document.createElement('button');
    okBtn.textContent = confirmLabel;
    okBtn.setAttribute('data-testid', 'bt-confirm-ok');
    const dangerBg = 'linear-gradient(180deg,#ef4444,#dc2626)';
    const primaryBg = 'linear-gradient(180deg,#F8D13F,#e5b800)';
    Object.assign(okBtn.style, {
      padding: '9px 20px', borderRadius: '999px', fontWeight: '800', fontSize: '13px',
      border: 'none',
      background: variant === 'danger' ? dangerBg : primaryBg,
      color: variant === 'danger' ? 'white' : '#1B2A4A',
      cursor: 'pointer', transition: 'transform .15s ease, box-shadow .15s ease',
      boxShadow: variant === 'danger' ? '0 0 14px -2px rgba(239,68,68,.55)' : '0 0 14px -2px rgba(248,209,63,.55)',
    });
    okBtn.onmouseenter = () => { okBtn.style.transform = 'translateY(-1px)'; };
    okBtn.onmouseleave = () => { okBtn.style.transform = 'translateY(0)'; };

    btnRow.appendChild(cancelBtn);
    btnRow.appendChild(okBtn);
    card.appendChild(btnRow);
    overlay.appendChild(card);

    document.body.appendChild(overlay);
    // Trigger transition
    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
      card.style.transform = 'scale(1) translateY(0)';
    });

    const cleanup = (result) => {
      overlay.style.opacity = '0';
      card.style.transform = 'scale(.95) translateY(6px)';
      setTimeout(() => { try { overlay.remove(); } catch {} }, 180);
      window.removeEventListener('keydown', onKey);
      resolve(result);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') cleanup(false);
      if (e.key === 'Enter') cleanup(true);
    };
    window.addEventListener('keydown', onKey);
    cancelBtn.onclick = () => cleanup(false);
    okBtn.onclick = () => cleanup(true);
    overlay.onclick = (e) => { if (e.target === overlay) cleanup(false); };

    // Focus the primary button for keyboard-first UX
    setTimeout(() => okBtn.focus(), 60);
  });
}

// Expose globally for legacy inline handlers
if (typeof window !== 'undefined') {
  window.btConfirm = btConfirm;
}
